/**
 * U-03 «Редактировать профиль».
 *
 * BS §17.4: `PATCH /me/profile` с `If-Match` по `version` (решение D-41).
 * Изменение v1.11: текстовые поля публикуются сразу; о снятом модерацией поле
 * пользователь узнаёт только в своём профиле (ПС §07).
 * SYS-14: ошибка подсвечивает конкретное поле, введённые значения сохраняются.
 */
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { api, ifMatch, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { FieldErrorCode, Interest, OwnProfile } from '@shared/api/types'
import { Button, Chip, Dialog, Field, Input, Textarea, useToast } from '@shared/ui'

import { CityPicker } from '@features/profile/CityPicker'
import { InterestsPicker } from '@features/profile/InterestsPicker'

import { Screen } from '../../layouts/Screen'
import { refreshMe } from '../../boot/bootstrap'
import { useAppStore } from '../../model/app-store'
import { useInterests } from './use-interests'
import styles from './profile.module.css'

/*
 * Длины полей заданы схемой контракта (`ProfilePatch`), а не настройкой:
 * у них нет ключа в `GET /config`, и сервер валидирует ровно эти значения.
 */
const BIO_MAX = 500
const NAME_MAX = 40

/** Запасное значение на случай, если конфигурация ещё не загружена. */
const INTERESTS_MAX_FALLBACK = 20

const FIELD_ERROR_TEXT: Record<FieldErrorCode, string> = {
  required: 'Обязательное поле',
  too_short: 'Слишком коротко',
  too_long: 'Слишком длинно',
  invalid_format: 'Неверный формат',
  out_of_range: 'Значение вне допустимого диапазона',
  not_allowed_word: 'Недопустимое содержимое',
  unknown_id: 'Неизвестное значение',
  immutable: 'Поле нельзя изменить',
  duplicate: 'Уже используется',
  conflict: 'Конфликт значений',
}

export function ProfileEditScreen(): React.ReactElement {
  const navigate = useNavigate()
  const toast = useToast()

  const me = useAppStore((s) => s.me)
  const config = useAppStore((s) => s.config)
  const interests = useInterests()

  // Предел выбора — из `GET /config → limits.interests_max` (BS §17.4).
  const interestsMax = config?.limits.interests_max ?? INTERESTS_MAX_FALLBACK

  const [displayName, setDisplayName] = useState(me?.profile.display_name ?? '')
  const [bio, setBio] = useState(me?.profile.bio ?? '')
  const [occupation, setOccupation] = useState(me?.profile.occupation ?? '')
  const [selected, setSelected] = useState<string[]>(me?.profile.interests.map((i) => i.id) ?? [])

  const [errors, setErrors] = useState<Map<string, FieldErrorCode>>(new Map())
  const [saving, setSaving] = useState(false)
  /** U-10: попытка уйти с несохранёнными изменениями. */
  const [leaveOpen, setLeaveOpen] = useState(false)
  /** U-11: значения с другого устройства, поверх которых предлагается записать свои. */
  const [conflict, setConflict] = useState<OwnProfile | null>(null)
  const [interestsOpen, setInterestsOpen] = useState(false)
  const [cityId, setCityId] = useState(me?.profile.city?.id ?? '')
  const [cityName, setCityName] = useState(me?.profile.city?.name ?? '')
  const [cityOpen, setCityOpen] = useState(false)

  if (!me)
    return (
      <Screen title="Редактирование" back>
        {null}
      </Screen>
    )

  /** Выбранные — в порядке справочника, чтобы список не «прыгал». */
  const chosen = interests.filter((interest) => selected.includes(interest.id))

  const toggleInterest = (interest: Interest): void => {
    setSelected((current) =>
      current.includes(interest.id)
        ? current.filter((id) => id !== interest.id)
        : current.length >= interestsMax
          ? current
          : [...current, interest.id],
    )
  }

  const save = async (): Promise<void> => {
    setSaving(true)
    setErrors(new Map())

    try {
      unwrap(
        await api.PATCH('/me/profile', {
          body: {
            display_name: displayName.trim(),
            bio: bio.trim(),
            occupation: occupation.trim(),
            interest_ids: selected,
            // `city_id` — алиас `PUT /me/location` (источник manual, BS §11.2).
            ...(cityId ? { city_id: cityId } : {}),
          } as never,
          // Optimistic concurrency — единственные операции контракта с If-Match.
          ...ifMatch(me.profile.version),
        }),
      )

      await refreshMe()
      toast.show('Профиль сохранён')
      void navigate({ to: '/me' })
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.code === 'validation_failed') {
          setErrors(error.fieldErrors())
        } else if (error.code === 'conflict') {
          /*
           * U-11: профиль изменён с другого устройства. Молча затирать чужие
           * правки нельзя, но и терять свои — тоже: показываем актуальные
           * значения и даём выбрать (ПС §19 «Конфликт состояния»).
           */
          await refreshMe()
          const fresh = useAppStore.getState().me?.profile
          if (fresh) setConflict(fresh)
        } else {
          toast.show('Не удалось сохранить', { tone: 'danger' })
        }
      } else {
        toast.show('Нет соединения. Изменения не сохранены.', { tone: 'danger' })
      }
    } finally {
      setSaving(false)
    }
  }

  /** U-11: «применить свои поверх» — повтор с версией, которую вернул сервер. */
  const overwrite = async (): Promise<void> => {
    const fresh = conflict
    setConflict(null)
    if (!fresh) return

    setSaving(true)
    try {
      unwrap(
        await api.PATCH('/me/profile', {
          body: {
            display_name: displayName.trim(),
            bio: bio.trim(),
            occupation: occupation.trim(),
            interest_ids: selected,
            ...(cityId ? { city_id: cityId } : {}),
          } as never,
          ...ifMatch(fresh.version),
        }),
      )
      await refreshMe()
      toast.show('Профиль сохранён')
      void navigate({ to: '/me' })
    } catch {
      toast.show('Не удалось сохранить', { tone: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  /** U-11: «отменить свои» — берём значения сервера в поля. */
  const acceptServer = (): void => {
    const fresh = conflict
    setConflict(null)
    if (!fresh) return

    setDisplayName(fresh.display_name ?? '')
    setBio(fresh.bio ?? '')
    setOccupation(fresh.occupation ?? '')
    setSelected(fresh.interests.map((i) => i.id))
    setCityId(fresh.city?.id ?? '')
    setCityName(fresh.city?.name ?? '')
  }

  /** U-10: есть ли что терять при уходе. */
  const dirty =
    displayName !== (me.profile.display_name ?? '') ||
    bio !== (me.profile.bio ?? '') ||
    occupation !== (me.profile.occupation ?? '') ||
    selected.join(',') !== me.profile.interests.map((i) => i.id).join(',') ||
    cityId !== (me.profile.city?.id ?? '')

  const leave = (): void => {
    if (dirty) setLeaveOpen(true)
    else void navigate({ to: '/me' })
  }

  const errorFor = (field: string): string | undefined => {
    const code = errors.get(field)
    return code ? FIELD_ERROR_TEXT[code] : undefined
  }

  return (
    <Screen
      title="Профиль"
      back={leave}
      narrow
      actions={
        <Button variant="ghost" size="sm" loading={saving} disabled={!dirty} onClick={() => void save()}>
          Сохранить
        </Button>
      }
    >
      <div className={styles['formStack']}>
        <Field
          label="Имя или ник"
          error={errorFor('display_name')}
          counter={{ value: displayName.length, max: NAME_MAX }}
          hint={
            me.profile.field_states.display_name.state === 'rejected'
              ? 'Снято проверкой — другим не показывается'
              : undefined
          }
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              value={displayName}
              maxLength={NAME_MAX}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="О себе"
          error={errorFor('bio')}
          counter={{ value: bio.length, max: BIO_MAX }}
          hint={
            me.profile.field_states.bio.state === 'rejected'
              ? 'Снято проверкой — другим не показывается'
              : 'Несколько строк о себе'
          }
        >
          {({ id, describedBy, invalid }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              value={bio}
              maxLength={BIO_MAX}
              onChange={(event) => setBio(event.target.value)}
              rows={5}
            />
          )}
        </Field>

        {/* O-08 / U-03: город — единственная форма местоположения в интерфейсе */}
        <Field label="Город" error={errorFor('city_id')}>
          {() => (
            <div className={styles['chips']}>
              {cityName ? (
                <Chip selected icon="pin" onClick={() => setCityOpen(true)}>
                  {cityName}
                </Chip>
              ) : null}
              <Chip icon={cityName ? 'edit' : 'search'} onClick={() => setCityOpen(true)}>
                {cityName ? 'Изменить' : 'Выбрать город'}
              </Chip>
            </div>
          )}
        </Field>

        <Field label="Род занятий" error={errorFor('occupation')}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              value={occupation}
              maxLength={60}
              onChange={(event) => setOccupation(event.target.value)}
              placeholder="Например: продуктовый дизайнер"
            />
          )}
        </Field>

        <Field
          label="Интересы"
          hint={`Выбрано ${selected.length} из ${interestsMax}`}
          error={errorFor('interest_ids')}
        >
          {() => (
            <div className={styles['chips']}>
              {chosen.map((interest) => (
                <Chip key={interest.id} selected onClick={() => toggleInterest(interest)}>
                  {interest.label}
                </Chip>
              ))}

              {/* U-13: справочник целиком — отдельным листом с поиском */}
              <Chip icon="plus" onClick={() => setInterestsOpen(true)}>
                {chosen.length ? 'Изменить' : 'Выбрать интересы'}
              </Chip>
            </div>
          )}
        </Field>
      </div>

      {/* U-12: дата рождения принимается один раз (BS §5.7, `birth_date_locked`) */}
      {me.profile.birth_date_set ? (
        <p className={styles['footnote']}>
          Дату рождения меняет только поддержка — на ней держится проверка возраста.{' '}
          <button
            type="button"
            className={styles['linkButton']}
            onClick={() => void navigate({ to: '/settings/support/new' })}
          >
            Написать в поддержку
          </button>
        </p>
      ) : null}

      {/* --- O-08 «Выбор города» --- */}
      <CityPicker
        open={cityOpen}
        selectedId={cityId || null}
        onSelect={(city) => {
          setCityId(city.id)
          setCityName(city.name)
        }}
        onClose={() => setCityOpen(false)}
      />

      {/* --- U-13 «Выбор интересов» --- */}
      <InterestsPicker
        open={interestsOpen}
        interests={interests}
        selected={selected}
        max={interestsMax}
        onToggle={toggleInterest}
        onClose={() => setInterestsOpen(false)}
      />

      {/* --- U-10 «Несохранённые изменения» --- */}
      <Dialog
        open={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        title="Не сохранять изменения?"
        description="Вы изменили профиль, но не сохранили."
        footer={
          <>
            <Button variant="ghost" onClick={() => setLeaveOpen(false)}>
              Остаться
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setLeaveOpen(false)
                void navigate({ to: '/me' })
              }}
            >
              Не сохранять
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setLeaveOpen(false)
                void save()
              }}
            >
              Сохранить
            </Button>
          </>
        }
      />

      {/* --- U-11 «Конфликт версии профиля» --- */}
      <Dialog
        open={conflict !== null}
        onClose={() => setConflict(null)}
        title="Профиль изменён на другом устройстве"
        description="Мы не стали затирать те изменения. Выберите, что оставить."
        footer={
          <>
            <Button variant="secondary" onClick={acceptServer}>
              Взять с сервера
            </Button>
            <Button variant="primary" loading={saving} onClick={() => void overwrite()}>
              Оставить мои
            </Button>
          </>
        }
      >
        <dl className={styles['conflict']}>
          <ConflictRow label="Имя" mine={displayName} theirs={conflict?.display_name ?? ''} />
          <ConflictRow label="О себе" mine={bio} theirs={conflict?.bio ?? ''} />
          <ConflictRow label="Род занятий" mine={occupation} theirs={conflict?.occupation ?? ''} />
        </dl>
      </Dialog>
    </Screen>
  )
}

/** U-11: показываем обе версии значения — решение принимает пользователь. */
function ConflictRow({
  label,
  mine,
  theirs,
}: {
  label: string
  mine: string
  theirs: string
}): React.ReactElement | null {
  if (mine.trim() === theirs.trim()) return null

  return (
    <div className={styles['conflictRow']}>
      <dt className={styles['conflictLabel']}>{label}</dt>
      <dd className={styles['conflictValues']}>
        <span className={styles['conflictMine']}>{mine.trim() || '—'}</span>
        <span className={styles['conflictTheirs']}>на сервере: {theirs.trim() || '—'}</span>
      </dd>
    </div>
  )
}
